import {
  DraftMerchant,
  DraftMerchantStatusEnum,
  DraftMerchantUserStatusEnum,
  Industry,
  UpdateDraftMerchantSite,
} from "@tableyeti/merchant-service";
import { AxiosError } from "axios";
import { defineStore } from "pinia";
import { useYetipayApiNotAuthenticated } from "@/composables/useYetipayApi";
import { useApplicationStore } from "@/store/application";

const applicationStore = useApplicationStore();

export class OnboardingError extends Error {
  field: string;

  constructor(message: string, field: string) {
    super(message);
    this.name = "OnboardingError";
    this.field = field;
  }
}

export enum LOADING_STATES {
  NOT_LOADED = "NOT_LOADED",
  LOADED = "LOADED",
}

type DeviceBasketItem = "s1f2" | "s1f2Dock" | "s1e2";

export type DeviceBasket = { [device in DeviceBasketItem]?: number };

export enum OnboardingFlowStatusEnum {
  SiteEdit = "site-edit",
  SignUp = "signup",
}

export type OnboardingStatus = DraftMerchantStatusEnum | OnboardingFlowStatusEnum;

type OnboardingStore = {
  state: {
    shortcode?: string;
    email?: string;
    inviteToken?: string;
    draftMerchant?: DraftMerchant;
    api: ReturnType<typeof useYetipayApiNotAuthenticated>["api"];
    overrideStatus?: OnboardingStatus;
    initialiseState: LOADING_STATES;
  };
  getters: {
    onboardingStatus(): OnboardingStatus;
  };
  actions: {
    initialize(): Promise<undefined>;
    setDraftMerchant(draftMerchant: DraftMerchant): void;
    clearDraftMerchant(): void;
    clearSavedDraftMerchant(): void;
    setDraftMerchant(draftMerchant: DraftMerchant): void;
    setStatusOverride(status: OnboardingStatus): void;
    clearOverrideStatus: () => void;
    setError(message?: string): void;
    fetchOrCreateDraftMerchant(
      shortcode: string,
      email: string,
    ): Promise<DraftMerchant | undefined>;
    fetchDraftMerchant(): Promise<DraftMerchant | undefined>;
    updateDraftMerchant(details: Partial<DraftMerchant>): Promise<DraftMerchant>;
    submitDraftMerchant(): Promise<DraftMerchant>;
    getIndustries(): Promise<Array<Industry>>;
    updateDraftMerchantSite(
      siteDetails: Partial<UpdateDraftMerchantSite>,
      deviceOrder: DeviceBasket,
    ): Promise<DraftMerchant>;
    submitDraftMerchantSite(): Promise<DraftMerchant>;
    isInitialising(): boolean;
    updateEmail(email: string, inviteToken?: string): Promise<DraftMerchant>;
    acceptTermsAndConditions(): Promise<DraftMerchant>;
    getDraftMerchantFromInvite(shortcode: string, inviteToken: string): Promise<DraftMerchant>;
  };
};

// TODO: implement as composable
export const useOnboardingStore = defineStore<
  "onboarding",
  OnboardingStore["state"],
  OnboardingStore["getters"],
  OnboardingStore["actions"]
>("onboarding", {
  state: () => {
    return {
      shortcode: undefined,
      email: undefined,
      inviteToken: undefined,
      draftMerchant: undefined,
      overrideStatus: undefined,
      api: useYetipayApiNotAuthenticated().api,
      initialiseState: LOADING_STATES.NOT_LOADED,
    };
  },
  getters: {
    onboardingStatus() {
      return this.overrideStatus ?? this.draftMerchant?.status ?? DraftMerchantStatusEnum.Created;
    },
  },
  actions: {
    async initialize() {
      // TODO: discuss security improvements
      const shortcode = localStorage.getItem("yetipay-onboarding-shortcode");
      const email = localStorage.getItem("yetipay-onboarding-email");
      const inviteToken = localStorage.getItem("yetipay-onboarding-invite");
      if (shortcode && email) {
        try {
          const draftMerchant = (await this.api.getDraftMerchant(email, shortcode)).data;
          this.setDraftMerchant(draftMerchant);
        } catch (error) {
          console.error("Error retrieving onboarding session with local storage data", { error });
          this.clearDraftMerchant();
        }
      }
      if (inviteToken) {
        this.inviteToken = inviteToken;
        const url = new URL(window.location.href);
        url.searchParams.set("invite", inviteToken);
        window.history.replaceState({}, "", url.toString());
      }
      this.initialiseState = LOADING_STATES.LOADED;
    },
    setDraftMerchant(draftMerchant: DraftMerchant) {
      this.draftMerchant = draftMerchant;
      this.shortcode = draftMerchant.dealShortCode;
      this.email = draftMerchant.email;
      localStorage.setItem("yetipay-onboarding-shortcode", draftMerchant.dealShortCode);
      localStorage.setItem("yetipay-onboarding-email", draftMerchant.email);
    },
    clearDraftMerchant() {
      localStorage.removeItem("yetipay-onboarding-shortcode");
      localStorage.removeItem("yetipay-onboarding-email");
      localStorage.removeItem("yetipay-onboarding-invite");
      this.shortcode = undefined;
      this.email = undefined;
      this.draftMerchant = undefined;
      this.overrideStatus = undefined;
      if (this.inviteToken) {
        this.inviteToken = undefined;
        const url = new URL(window.location.href);
        url.searchParams.delete("invite");
        window.history.replaceState({}, "", url.toString());
      }
      this.setError(); // clear any error message
    },
    clearSavedDraftMerchant() {
      localStorage.removeItem("yetipay-onboarding-shortcode");
      localStorage.removeItem("yetipay-onboarding-email");
      localStorage.removeItem("yetipay-onboarding-invite");
    },
    setStatusOverride(status: OnboardingStatus) {
      this.overrideStatus = status;
    },
    clearOverrideStatus() {
      this.overrideStatus = undefined;
    },
    setError(message?: string) {
      if (message) {
        applicationStore.notifyUser({ message, type: "warning", timeout: -1, fullWidth: true });
      } else {
        applicationStore.clearUserNotification();
      }
    },
    async getIndustries(): Promise<Array<Industry>> {
      const res = await this.api.getIndustries(this.email, this.shortcode);
      return res.data.data;
    },
    async fetchDraftMerchant() {
      if (!this.shortcode || !this.email) {
        throw new Error("No onboarding session found");
      }

      try {
        const draftMerchant = (await this.api.getDraftMerchant(this.email, this.shortcode)).data;
        this.setDraftMerchant(draftMerchant);
        return draftMerchant;
      } catch (error) {
        if (error instanceof AxiosError && error.status !== 404) {
          console.error("Error fetching draft merchant", error);
          throw new OnboardingError(
            "Unable to start onboarding, please try again or contact support",
            "shortcode",
          );
        }
      }
    },
    async fetchOrCreateDraftMerchant(shortcode: string, email: string) {
      const emailLowerCase = email.toLowerCase();

      try {
        const draftMerchant = (await this.api.getDraftMerchant(emailLowerCase, shortcode)).data;
        this.setDraftMerchant(draftMerchant);
        return draftMerchant;
      } catch (error) {
        if (error instanceof AxiosError && error.status !== 404) {
          console.error("Error fetching draft merchant", error);
          throw new OnboardingError(
            "Unable to start onboarding, please try again or contact support",
            "shortcode",
          );
        }
      }

      try {
        const draftMerchant = (
          await this.api.createDraftMerchant({ dealShortCode: shortcode, email: emailLowerCase })
        ).data;
        this.setDraftMerchant(draftMerchant);
        return draftMerchant;
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.status === 404) {
            throw new OnboardingError(
              "Shortcode not recognised, please check it and try again",
              "shortcode",
            );
          }
          if (
            error.status === 400 &&
            error.response?.data?.message === "draft merchant already exists for this dealShortCode"
          ) {
            throw new OnboardingError(
              "Onboarding already started for this shortcode, please enter the email used previously",
              "email",
            );
          }
        }
        console.error("Error creating draft merchant", error);
        throw new OnboardingError(
          "Unable to start onboarding, please try again or contact support",
          "shortcode",
        );
      }
    },
    async updateDraftMerchant(details) {
      if (!this.shortcode || !this.email) {
        throw new Error("No onboarding session found");
      }

      try {
        const draftMerchant = (
          await this.api.updateDraftMerchantDetails(this.email, this.shortcode, details)
        ).data;
        this.draftMerchant = draftMerchant;
        return draftMerchant;
      } catch (error) {
        console.error("Error updating draft merchant", error);
        const errorMessage = error instanceof AxiosError ? error.response?.data.message : undefined;
        throw new OnboardingError(
          errorMessage ??
            "Unable to update onboarding details, please try again or contact support",
          "form",
        );
      }
    },
    async submitDraftMerchant() {
      if (!this.shortcode || !this.email) {
        throw new Error("No onboarding session found");
      }

      try {
        const draftMerchant = (
          await this.api.submitDraftMerchantDetails(this.email, this.shortcode)
        ).data;
        this.draftMerchant = draftMerchant;
        return draftMerchant;
      } catch (error) {
        console.error("Error submitting draft merchant", error);
        throw new OnboardingError(
          "Unable to submit onboarding details, please try again or contact support",
          "form",
        );
      }
    },
    async updateDraftMerchantSite(
      siteDetails: Partial<UpdateDraftMerchantSite>,
      deviceOrder: { [key: string]: number },
    ) {
      if (!this.shortcode || !this.email) {
        throw new Error("No onboarding session found");
      }

      const updateRequest: UpdateDraftMerchantSite = {
        ...siteDetails,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        basket: Object.fromEntries(Object.entries(deviceOrder).filter(([_, value]) => value > 0)),
      };
      updateRequest.displayName = siteDetails.displayName || undefined; // In case the input field is cleared, we don't want to send an empty string

      try {
        const draftMerchant = (
          await this.api.updateDraftMerchantSite(this.email, this.shortcode, updateRequest)
        ).data;
        this.draftMerchant = draftMerchant;
        return draftMerchant;
      } catch (error) {
        console.error("Error updating draft merchant site", error);
        if (error instanceof AxiosError) {
          throw new OnboardingError(error.message, "devices");
        }
        throw new OnboardingError(
          "Unable to process device order, please try again or contact support",
          "devices",
        );
      }
    },
    async acceptTermsAndConditions() {
      if (!this.shortcode || !this.email) {
        throw new Error("No onboarding session found");
      }

      const updateRequest: UpdateDraftMerchantSite = {
        termsAccepted: true,
      };
      try {
        const draftMerchant = (
          await this.api.updateDraftMerchantSite(this.email, this.shortcode, updateRequest)
        ).data;
        this.draftMerchant = draftMerchant;
        return draftMerchant;
      } catch (error) {
        console.error("Error updating draft merchant site", error);
        if (error instanceof AxiosError) {
          throw new OnboardingError(error.message, "devices");
        }
        throw new OnboardingError(
          "Unable to process device order, please try again or contact support",
          "devices",
        );
      }
    },
    async submitDraftMerchantSite() {
      if (!this.shortcode || !this.email) {
        throw new Error("No onboarding session found");
      }

      try {
        await this.api.submitDraftMerchantSite(this.email, this.shortcode);

        const draftMerchant = await this.fetchDraftMerchant();
        if (
          draftMerchant?.userStatus === DraftMerchantUserStatusEnum.Confirmed ||
          draftMerchant?.userStatus === DraftMerchantUserStatusEnum.ExternalProvider
        ) {
          this.clearOverrideStatus();
        } else {
          this.setStatusOverride(OnboardingFlowStatusEnum.SignUp);
        }
        return draftMerchant!;
      } catch (error) {
        //let's fetch the latest object to make sure we have the latest state
        console.error("Error submitting draft merchant site", error);
        await this.fetchDraftMerchant().catch((e) =>
          console.error("Error fetching draft merchant site", e),
        );
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data.message;
          throw new OnboardingError(errorMessage, "devices");
        }
        throw new OnboardingError(
          "Unable to process device order, please try again or contact support",
          "devices",
        );
      }
    },
    async updateEmail(newEmail: string, inviteToken?: string) {
      try {
        await this.api.draftMerchantEmail(
          { email: newEmail },
          this.email,
          this.shortcode,
          inviteToken,
        );
        const draftMerchant = (await this.api.getDraftMerchant(newEmail, this.shortcode)).data;
        this.setDraftMerchant(draftMerchant);
        if (inviteToken) {
          localStorage.setItem("yetipay-onboarding-invite", inviteToken);
        }
        return draftMerchant;
      } catch (error) {
        if (error instanceof AxiosError) {
          throw new OnboardingError(error.message, "signup");
        }
        throw new OnboardingError("Unable to update email", "signup");
      }
    },
    isInitialising() {
      return this.initialiseState === LOADING_STATES.NOT_LOADED;
    },
    async getDraftMerchantFromInvite(shortcode: string, inviteToken: string) {
      try {
        const { data: draftMerchant } = await this.api.getOnboardingInvite(inviteToken, shortcode);
        this.draftMerchant = draftMerchant;
        this.shortcode = draftMerchant.dealShortCode;
        return draftMerchant;
      } catch (error) {
        console.log(error);
        if (error instanceof AxiosError) {
          throw new OnboardingError(error.response?.data.message, "invite");
        }
        throw new OnboardingError("Unable to retrieve invite", "invite");
      }
    },
  },
});
